import React, { useCallback, useState, useRef } from "react";
import PropTypes from "prop-types";
import SchemaForm from "@rjsf/core";
import injectSheet from "react-jss";
import { useTranslation } from "react-i18next";
import Hint from "components/Hint";
import Markdown from "components/Markdown";
import get from "lodash.get";
import DescriptionField from "customization/fields/DescriptionField";
//import InfoField from "customization/fields/InfoField";
import TitleField from "customization/fields/TitleField";
import FieldTemplate from "customization/templates/FieldTemplate";
import ObjectFieldTemplate from "customization/templates/ObjectFieldTemplate";
import ArrayFieldTemplate from "customization/templates/ArrayFieldTemplate";
import BaseInput from "customization/widgets/BaseInput";
import RadioWidget from "customization/widgets/RadioWidget";
import TextareaWidget from "customization/widgets/TextareaWidget";
import SelectWidget from "customization/widgets/SelectWidget";
import ToggleButtonWidget from "customization/widgets/ToggleButtonWidget";
import CheckboxWidget from "customization/widgets/CheckboxWidget";
import CheckboxesWidget from "customization/widgets/CheckboxesWidget";
import FileWidget from "customization/widgets/FileWidget";
import DateWidget from "customization/widgets/DateWidget";
import StepFormFooter from "./StepFormFooter";
import ViewportPadding from "components/ViewportPadding";
import { useFileSize } from "providers/FileSizeProvider";
import { MAX_ATTACHMENT_SIZE } from "providers/FileSizeProvider/FileSizeProvider";
import { flattenObject } from "services/utils.service";

/**
 * Overrides native widgetrs from react-json-schemaform
 * See https://react-jsonschema-form.readthedocs.io/en/latest/advanced-customization/#custom-widgets-and-fields
 */
const widgets = {
  BaseInput: BaseInput,
  RadioWidget: RadioWidget,
  TextareaWidget: TextareaWidget,
  SelectWidget: SelectWidget,
  CheckboxWidget: CheckboxWidget,
  CheckboxesWidget: CheckboxesWidget,
  FileWidget: FileWidget,
  DateWidget: DateWidget,
  toggle: ToggleButtonWidget,
};

/**
 * Overrides native fields from react-jsonschema-form.
 * See https://react-jsonschema-form.readthedocs.io/en/latest/advanced-customization/#custom-field-components
 */
const fields = {
  DescriptionField: DescriptionField,
  TitleField: TitleField,
  //info: InfoField,
};

const styles = ({ theme }) => ({
  description: {
    marginBottom: theme.margin.sm,
  },
});

const StepForm = ({
  classes,
  onSubmit,
  onChange,
  index,
  schema,
  uiSchema,
  formData,
  disabled,
  description,
  data,
}) => {
  const [hasTriedSubmit, setHasTriedSubmit] = useState(false);
  const formRef = useRef();
  const [{ attachmentsSize }] = useFileSize();
  const { t } = useTranslation();
  const transformErrors = useCallback(
    (errors) =>
      errors.map((error) => {
        // console.debug("error", JSON.stringify(error, null, 4));

        if (error.name === "required") {
          error.message = t("error.required.label");
        }
        if (error.name === "enum") {
          error.message = t("error.enum.label");
          error.message = null;
        }
        if (error.name === "oneOf") {
          error.message = t("error.oneOf.label");
          error.message = null;
        }
        if (error.name === "format") {
          error.message = t("error.dateformat.label");
        }
        if (error.name === "pattern") {
          error.message = t("error.pattern.label");
        }
        return error;
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function validate(data, errors) {
    /**
     * react-jsonschema-form does not have built in support for limiting filesize
     * plus, we need to limit the total filesize of the entire form, not just a single stepform
     */

    if (attachmentsSize > MAX_ATTACHMENT_SIZE) {
      // get the paths to all of the fields that contain some "data:" as their value
      const dataFields = Object.entries(flattenObject(data)).reduce(
        (acc, [key, value]) => {
          if (/^data:/.test(value)) {
            acc.push(key.replace(/\[\w*\]$/, ""));
          }
          return acc;
        },
        []
      );
      // get unique so that an input that has multiple only gives us one error message
      // since it should be considered as __one__ file input
      const uniqueDataFields = [...new Set(dataFields)];
      uniqueDataFields.forEach((field) => {
        const errorsArray = get(errors, `${field}.__errors`, []);
        errorsArray.push(t("error.filesize.label"));
      });
    }

    return errors;
  }

  return (
    <ViewportPadding x={"md"}>
      {description ? (
        <div className={classes.description}>
          <Hint>
            <Markdown source={description} />
          </Hint>
        </div>
      ) : null}
      <SchemaForm
        ref={formRef}
        omitExtraData={true}
        disabled={disabled}
        onSubmit={onSubmit}
        onChange={onChange}
        schema={schema}
        formData={formData}
        uiSchema={uiSchema}
        //
        // Only use custom validation
        //
        liveValidate={true}
        noHtml5Validate
        validate={validate}
        onError={(e) => {
          setHasTriedSubmit(true);
          for (var input of formRef.current.formElement.elements) {
            if (!input.validity.valid) {
              input.scrollIntoView();
              input.focus();
              break;
            }
          }
        }}
        //
        // Dont show an aggregated list of errors, only at each field
        //
        showErrorList={false}
        //
        // Override core templates
        //
        FieldTemplate={FieldTemplate}
        ObjectFieldTemplate={ObjectFieldTemplate}
        ArrayFieldTemplate={ArrayFieldTemplate}
        //
        // Override core fields
        //
        fields={fields}
        //
        // Override core widgets
        //
        widgets={widgets}
        //
        // Transforms lib error messages
        //
        transformErrors={transformErrors}
        formContext={{ hasTriedSubmit }}
      >
        <StepFormFooter />
      </SchemaForm>
    </ViewportPadding>
  );
};

StepForm.defaultProps = {
  disabled: false,
};

StepForm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  formData: PropTypes.object,
  disabled: PropTypes.bool.isRequired,
  description: PropTypes.string,
};

export default injectSheet(styles)(StepForm);
